// A score as the reader sees it. The engine stores every score for the side
// to move; a `Score` is turned to one side once, when it is made, and from
// then on every number, text and colour a plugin shows comes from its
// methods, so no two places can format or sign it differently.
import { type Color, type Fen } from '@chess/api-model';

/** An engine score as the scan stores it: centipawns, or moves to mate, for the side to move. */
export interface EngineSideScore {
  cp?: number;
  mate?: number;
}

/**
 * A score seen from one side. A finished game's last position carries
 * `mate: 0`, which only says the side to move is mated; `mated` keeps who.
 */
export interface ReaderScore extends EngineSideScore {
  mated?: 'you' | 'them';
}

/** A forced mate counts as this many pawns, as the score bar has it. */
export const MATE_PAWNS = 128;
/** How many pawns of advantage give most of a bar's travel. */
export const PAWNS_PER_BAR = 4;

export function sideToMove(fen: Fen): Color {
  return fen.split(' ')[1] === 'b' ? 'black' : 'white';
}

export class Score implements ReaderScore {
  readonly cp?: number;
  readonly mate?: number;
  readonly mated?: 'you' | 'them';

  private constructor(value: ReaderScore) {
    // `mate: 0` alone says the side it is seen from is mated.
    const mated = value.mated ?? (value.mate === 0 ? 'you' : undefined);
    if (mated !== undefined) {
      this.mate = 0;
      this.mated = mated;
    } else if (value.mate !== undefined) {
      this.mate = value.mate;
    } else {
      this.cp = value.cp ?? 0;
    }
  }

  /** A level position: the start of a game, whatever the engine thinks of the first move. */
  static level(): Score {
    return new Score({ cp: 0 });
  }

  /** A score already seen from the side that matters. */
  static of(value: ReaderScore): Score {
    return value instanceof Score ? value : new Score(value);
  }

  /** A score in pawns from the side that matters. */
  static ofPawns(pawns: number): Score {
    return new Score({ cp: Math.round(pawns * 100) });
  }

  /** An engine score for the side to move (`mover`), seen from `side`. */
  static fromEngine(score: EngineSideScore, mover: Color, side: Color): Score {
    if (score.mate === 0) return new Score({ mated: mover === side ? 'you' : 'them' });
    if (mover === side) return new Score(score);
    if (score.mate !== undefined) return new Score({ mate: -score.mate });
    return new Score({ cp: -(score.cp ?? 0) });
  }

  /** An engine score stored with its position, seen from `side`. */
  static at(position: { fen: Fen; score: EngineSideScore }, side: Color): Score {
    return Score.fromEngine(position.score, sideToMove(position.fen), side);
  }

  get isMate(): boolean {
    return this.mate !== undefined;
  }

  /** In pawns; mates saturate at `MATE_PAWNS`. */
  pawns(): number {
    if (this.mated !== undefined) return this.mated === 'them' ? MATE_PAWNS : -MATE_PAWNS;
    if (this.mate !== undefined) return this.mate > 0 ? MATE_PAWNS : -MATE_PAWNS;
    return (this.cp ?? 0) / 100;
  }

  /** In centipawns, mates counted as nearly won or lost; for comparing two scores. */
  centipawns(): number {
    if (this.mated !== undefined) return this.mated === 'them' ? 10000 : -10000;
    if (this.mate !== undefined) return this.mate > 0 ? 10000 - this.mate : -10000 - this.mate;
    return this.cp ?? 0;
  }

  /** The app's notation: `+0.34`, `-1.20`, `M4`, `-M2`, and `mate` / `mated` for a finished game. */
  text(): string {
    if (this.mated !== undefined) return this.mated === 'them' ? 'mate' : 'mated';
    if (this.mate !== undefined) return this.mate > 0 ? `M${this.mate}` : `-M${-this.mate}`;
    const value = (this.cp ?? 0) / 100;
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}`;
  }

  /** The same score seen from the other side. */
  flipped(): Score {
    if (this.mated !== undefined) return new Score({ mated: this.mated === 'you' ? 'them' : 'you' });
    if (this.mate !== undefined) return new Score({ mate: -this.mate });
    return new Score({ cp: -(this.cp ?? 0) });
  }

  /** `this − other` in pawns. */
  minus(other: Score): number {
    return this.pawns() - other.pawns();
  }

  /** Good, bad or level for the side it is seen from. */
  tone(): 'good' | 'bad' | 'level' {
    const pawns = this.pawns();
    return pawns > 0.005 ? 'good' : pawns < -0.005 ? 'bad' : 'level';
  }

  /** The share of a bar this side fills, 0…1; 0.5 is level. */
  share(): number {
    return 0.5 + 0.5 * Math.tanh(this.pawns() / PAWNS_PER_BAR);
  }

  /** Plain data, for storing or sending. */
  toJSON(): ReaderScore {
    if (this.mated !== undefined) return { mate: 0, mated: this.mated };
    if (this.mate !== undefined) return { mate: this.mate };
    return { cp: this.cp };
  }
}
